import React, { useState } from "react";
import {
  Box,
  Button,
  Icon,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Text,
} from "@salestrak/ui";
import type { NoteReaction } from "./activity-types";
import { EmojiPicker } from "./emoji-picker";
import type { Omittable } from "@salestrak/ui";

type NoteReactionsProps = Omittable<{
  reactions: NoteReaction[];
  currentUserId: string | null;
  onToggleReaction: (emoji: string) => void;
  /** When true, existing reactions render read-only and the quick
   * thumbs-up + emoji-picker affordances are hidden. Drives the
   * view-only access tier in the issue view. */
  readOnly?: boolean;
}>;

export const NoteReactions = React.memo(function NoteReactions({
  reactions,
  currentUserId,
  onToggleReaction,
  readOnly = false,
}: NoteReactionsProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const hasUserReacted = (reaction: NoteReaction) => {
    if (!currentUserId || !reaction.userIds) return false;
    return reaction.userIds.includes(currentUserId);
  };

  const hasThumbsUp = reactions.some(
    (r) => r.emoji === "👍" && hasUserReacted(r),
  );

  const handleEmojiSelect = (emoji: string) => {
    onToggleReaction(emoji);
    setPickerOpen(false);
  };

  return (
    <Box direction="row" gap="xs" align="center" wrap>
      {reactions.map((reaction, index) => (
        <Button
          key={`${reaction.emoji}-${index}`}
          variant={hasUserReacted(reaction) ? "outline" : "ghost"}
          size="xs"
          className="rounded-full"
          onClick={
            readOnly ? undefined : () => onToggleReaction(reaction.emoji)
          }
        >
          <Text size="xs">{reaction.emoji}</Text>
          <Text size="xs" weight="medium">
            {reaction.count}
          </Text>
        </Button>
      ))}

      {/* Add-reaction affordances (quick thumbs-up + emoji picker)
          hide entirely under read-only — reactions are comments. */}
      {!readOnly && !hasThumbsUp && (
        <Button
          variant="ghost"
          size="icon-xs"
          className="rounded-full"
          onClick={() => onToggleReaction("👍")}
          aria-label="React with thumbs up"
        >
          <Icon name="thumbs-up" />
        </Button>
      )}

      {!readOnly && (
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger
            render={
              <Button
                variant="ghost"
                size="icon-xs"
                className="rounded-full"
                aria-label="Add reaction"
              />
            }
          >
            <Icon name="smile-plus" />
          </PopoverTrigger>
          <PopoverPopup side="top" align="start" className="w-66">
            <EmojiPicker onSelect={handleEmojiSelect} />
          </PopoverPopup>
        </Popover>
      )}
    </Box>
  );
});
